import { Button, ColumnTable } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { TranslationsKeys } from '../../../../../locales/translations';
import { StudentsRemovalRequestItemPending } from '../../types';
import { useHistory } from 'react-router-dom';
import { PathsLayouts } from '../../../../../config/routes';

export function useColumns(): ColumnTable<StudentsRemovalRequestItemPending>[] {
  const history = useHistory();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_MANAGEMENT_STUDENT_REMOVAL_REQUEST_MANAGEMENT_PENDING,
  );

  const goToStudentRemovalRequest = useCallback(
    (studyPlanEnrollmentId: number) => {
      history.push(
        `${PathsLayouts.studentsRemovalRequestsManage}/${studyPlanEnrollmentId}`,
      );
    },
    [history],
  );

  const table = useMemo(() => {
    return t<
      string,
      {
        headers: {
          studentName: string;
          studentIdentification: string;
          studyPlanVersionName: string;
          studyPlanEnrollmentStatus: string;
          requestedBy: string;
          action: string;
        };
        resolve: string;
      }
    >('requests-pending.table', {
      returnObjects: true,
    });
  }, [t]);

  return useMemo(
    () => [
      {
        columnName: 'student',
        headerText: table.headers.studentName,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            {row.student.accountLastNames}, {row.student.accountNames}
          </>
        ),
      },
      {
        columnName: 'student',
        headerText: table.headers.studentIdentification,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            {row.student.identificationTypeName}{' '}
            {row.student.identificationValueFormatted}
          </>
        ),
      },
      {
        columnName: 'studyPlanVersion',
        headerText: table.headers.studyPlanVersionName,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => <>{row.studyPlanVersion.name}</>,
      },
      {
        columnName: 'studyPlanEnrollment',
        headerText: table.headers.studyPlanEnrollmentStatus,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => <>{row.studyPlanEnrollment.statusName}</>,
      },
      {
        columnName: 'removalRequest',
        headerText: table.headers.requestedBy,
        tdClassName: 'text-center',
        thClassName: 'text-center',
        cellFormat: ({ row }) => (
          <>
            <div>
              {row.removalRequest.requestedBy.accountLastNames},{' '}
              {row.removalRequest.requestedBy.accountNames}
            </div>
            <div>
              {dayjs(row.removalRequest.requestedAt).format('DD/MM/YYYY')}
            </div>
          </>
        ),
      },
      {
        columnName: 'action',
        headerText: table.headers.action,
        tdClassName: 'text-center text-primary',
        thClassName: 'text-center',
        width: 120,
        cellFormat: ({ row }) => (
          <Button
            type="button"
            text={table.resolve}
            onClick={() =>
              goToStudentRemovalRequest(row.studyPlanEnrollment.id)
            }
            size="sm"
            color="primary"
          />
        ),
      },
    ],
    [table, goToStudentRemovalRequest],
  );
}
